import React, { useState } from 'react';
import {
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import RareButton from '../buttons/RareButton';
import ResultClassLabel from '../buttons/resultClassLabel';
import {
  capitalizeFirstLetter,
  interpretationColours,
  InterpretationEnum,
  interpretationLabels,
} from '../CustomHooks';

interface ReviewActionsPanelProps {
  onConfirm: () => void;
  onChange: (interpretation: InterpretationEnum, notes: string) => void;
  initialInterpretation: InterpretationEnum;
  changeActionSelected: boolean;
  onChangeActionSelected: (update: boolean) => void;
}

const INTERPRETATION_OPTIONS: readonly InterpretationEnum[] = [
  'POSITIVE',
  'NEGATIVE',
  'BORDERLINE',
  'INVALID',
] as const;

const ReviewActionsPanel: React.FC<ReviewActionsPanelProps> = ({
  onConfirm,
  onChange,
  initialInterpretation,
  changeActionSelected,
  onChangeActionSelected,
}) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [interpretation, setInterpretation] =
    useState<InterpretationEnum | null>();
  const [notes, setNotes] = useState('');

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setInterpretation(null);
    setNotes('');
    setOpenDialog(false);
  };

  const handleAccept = () => {
    if (interpretation && notes.trim()) {
      onChange(interpretation, notes);
      onChangeActionSelected(true);
      handleCloseDialog();
    }
  };

  return (
    <>
      {/* Top buttons */}
      <Box>
        <RareButton
          type="button"
          variant={!changeActionSelected ? 'success' : 'outlined'}
          fullWidth
          onClick={onConfirm}
          sx={{ maxWidth: '200px', minWidth: '200px' }}
        >
          {`Confirm ${capitalizeFirstLetter(
            interpretationLabels[
            initialInterpretation as keyof typeof interpretationLabels
            ]
          )}`}
        </RareButton>

        <RareButton
          type="button"
          variant={changeActionSelected ? 'cancel' : 'outlined'}
          fullWidth
          sx={{ marginY: 1, maxWidth: '200px', minWidth: '200px' }}
          onClick={handleOpenDialog}
        >
          Change
        </RareButton>
      </Box>

      {/* Change Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Select Interpretation</DialogTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {INTERPRETATION_OPTIONS.map((opt) => (
              <Box
                key={opt}
                onClick={() => setInterpretation(opt)}
                sx={{
                  cursor: 'pointer',
                  border:
                    interpretation === opt
                      ? '3px solid #1976d2'
                      : '3px solid transparent',
                  borderRadius: 1,
                  transition: '0.2s',
                  width: '60%',
                }}
              >
                <ResultClassLabel
                  labelcolour={interpretationColours[opt]}
                  value={
                    interpretationLabels[
                    opt as keyof typeof interpretationLabels
                    ]
                  }
                />
              </Box>
            ))}
          </Box>

          {interpretation && (
            <TextField
              label="Reviewer Notes"
              required
              multiline
              minRows={3}
              fullWidth
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleAccept}
            disabled={!interpretation || !notes.trim()}
            variant="contained"
          >
            Accept
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReviewActionsPanel;
